import { useState, useRef, useEffect, useCallback } from "react";
import type { ChangeEvent, MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import _ from "lodash";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import InputBase from "@material-ui/core/InputBase";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Link from "@material-ui/core/Link";
import { encryptInfo } from "../../utils/crypto";
import { loginSchema } from "../../utils/validations";
import { loginRequest } from "./Service";
import Snackbar from "../../components/Snackbar";
import { getUserBrowserAndDeviceInfo, getGeoLocation } from "../../utils/BrowserDetails";
import { useGlobalStyles } from "../../components/globalStyles";
import AuthPageBackground from "../../components/AuthPageBackground";
import { SESSION_EXPIRED_MESSAGE_KEY } from "../../constants/autoLogout";
import { authenticatorStrings, commonStrings, loginStrings } from "../../components/StringBundle";
import logoPng from "../../assets/images/logo_pdf.png";
import type { SnackState, LoginPayload, LoginResponse } from "./types";

// Login screen: email + password form that signs the user in and sends them to the 2FA screen.
export default function Login() {
  const classes = useGlobalStyles() as Record<string, string>;
  const navigate = useNavigate();

  const [name, setName] = useState<string>("");
  const [nameError, setNameError] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [passwordError, setPasswordError] = useState<string>("");
  const [showSnack, setSnack] = useState<SnackState>({ open: false, message: "", severity: "" });
  const [loading, setLoading] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Hides the current snackbar message. Also clears the auto-logout flag (a
  // no-op if it isn't set) — only once the user actually notices this
  // message should it stop surviving a reload (see the effect below).
  const closeSnack = () => {
    setSnack({ open: false, message: "", severity: "" });
    sessionStorage.removeItem("autoLogoutMessage");
    sessionStorage.removeItem(authenticatorStrings.sessionExpiredStorageKey);
    sessionStorage.removeItem(SESSION_EXPIRED_MESSAGE_KEY);
  };

  // Shows a message if the user was auto-logged-out. Doesn't clear the flag
  // here — getLogout's hard page reload (see AppBar.js) can race a second
  // reload (e.g. another in-flight request also hitting config/axios.js's
  // 401 handler), which would remount this component before the user ever
  // saw the message. Leaving the flag in sessionStorage until closeSnack
  // actually clears it means it keeps showing across any such reload.
  useEffect(() => {
    if (sessionStorage.getItem("autoLogoutMessage")) {
      // persist: true — this message should stay up until the user notices
      // it (closes it or starts typing), not vanish on the usual timer.
      setSnack({ open: true, message: loginStrings.youHaveBeenLoggedOut, severity: "success", persist: true });
    }
  }, []);

  // Shows why the user was bounced back here from the OTP screen (pending_token died mid-verification).
  useEffect(() => {
    if (sessionStorage.getItem(authenticatorStrings.sessionExpiredStorageKey)) {
      setSnack({ open: true, message: authenticatorStrings.sessionExpired, severity: "error", persist: true });
    }
  }, []);

  // Shows why the user was bounced back here after a 401 ended their session (see config/axios.js's forceLogout).
  useEffect(() => {
    if (sessionStorage.getItem(SESSION_EXPIRED_MESSAGE_KEY)) {
      setSnack({ open: true, message: authenticatorStrings.sessionExpired, severity: "error", persist: true });
    }
  }, []);

  // Keeps the cursor at the end of the password field when show/hide is toggled.
  useEffect(() => {
    if (inputRef.current?.value) {
      inputRef.current.selectionStart = inputRef.current.value.length;
      inputRef.current.selectionEnd = inputRef.current.value.length;
    }
  }, [showPassword]);

  // Fetches the user's location once when the page loads.
  useEffect(() => {
    getGeoLocation();
  }, []);

  // Validates the form, encrypts the credentials, and logs the user in.
  const onLogin = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const value = loginSchema.validate({ name, password }, { errors: { escapeHtml: true } });
    if (value?.error?.details.length) {
      if (value.error.details[0].path.includes("password")) {
        setPasswordError(loginStrings.passwordRequired);
        setNameError("");
      } else {
        setNameError(loginStrings.invalidName);
      }
      return;
    }

    setLoading(true);
    try {
      const encryptedUsername = encryptInfo(name);
      const encryptedPassword = encryptInfo(password);
      const browserInfo = getUserBrowserAndDeviceInfo();
      const data: LoginPayload = {
        username: encryptedUsername,
        password: encryptedPassword,
        device: browserInfo?.device,
        browser_info: browserInfo?.browser,
        location_info: `${localStorage.getItem("latitude")},${localStorage.getItem("longitude")}`,
      };
      const response: LoginResponse = await loginRequest(data);
      if (!_.isEmpty(response) && response.pending_token) {
        // Pass pending token, MFA flag, and preference via router state, not URL params or localStorage.
        navigate("/authenticator", {
          state: {
            pendingToken: response.pending_token,
            mfaEnrolled: response.mfa_enrolled,
            preferenceName: encryptedUsername,
          },
        });
      }
    } catch (e: unknown) {
      // caught errors have no known shape — narrow with a type guard before use, don't assume
      const serverMessage = axios.isAxiosError(e)
        ? (e.response?.data as { error?: string } | undefined)?.error
        : undefined;
      setSnack({
        open: true,
        message: serverMessage || loginStrings.genericError,
        severity: "error",
      });
      setTimeout(() => setLoading(false), 2000);
    }
  };

  // Shows or hides the typed password.
  const handleClickShowPassword = useCallback(() => {
    setShowPassword((show) => !show);
    inputRef.current?.focus();
  }, []);

  // Stops the password field from losing focus when the eye icon is clicked.
  const handleMouseDownPassword = (e: MouseEvent<HTMLButtonElement>) => e.preventDefault();

  // Updates the email field as the user types.
  const onChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setNameError("");
  };

  // Updates the password field as the user types.
  const onChangePassword = (e: ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  return (
    <div className={classes.loginPage}>
      <AuthPageBackground />
      <div className={classes.loginCard}>
        <div className={classes.loginImagePanel} aria-hidden="true" />

        <div className={classes.loginFormPanel}>
          {showSnack.open && (
            <Snackbar
              severity={showSnack.severity}
              message={showSnack.message}
              disableAutoHide={showSnack.persist}
              handleClose={closeSnack}
            />
          )}

          <img src={logoPng} alt={commonStrings.logoAlt} className={classes.loginLogo} />
          <Typography className={classes.loginSubtitle}>{loginStrings.subtitle}</Typography>

          <form className={classes.loginForm} noValidate>
            <div className={classes.loginField}>
              <label htmlFor="login-email" className={classes.loginLabel}>{loginStrings.emailAddress}</label>
              <div className={`${classes.loginInputWrap}${nameError ? ` ${classes.loginInputError}` : ""}`}>
                <MailOutlineIcon className={classes.loginInputIcon} />
                <InputBase
                  id="login-email"
                  name="name"
                  type="text"
                  autoComplete="username"
                  fullWidth
                  value={name}
                  placeholder={loginStrings.emailPlaceholder}
                  onChange={onChangeEmail}
                  onFocus={closeSnack}
                />
              </div>
              {!!nameError && <span className={classes.loginErrorText}>{nameError}</span>}
            </div>

            <div className={classes.loginField}>
              <label htmlFor="login-password" className={classes.loginLabel}>{loginStrings.password}</label>
              <div className={`${classes.loginInputWrap}${passwordError ? ` ${classes.loginInputError}` : ""}`}>
                <LockOutlinedIcon className={classes.loginInputIcon} />
                <InputBase
                  id="login-password"
                  inputRef={inputRef}
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  fullWidth
                  value={password}
                  placeholder={loginStrings.passwordPlaceholder}
                  onChange={onChangePassword}
                  onFocus={closeSnack}
                />
                <IconButton
                  type="button"
                  className={classes.loginEyeBtn}
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  disableRipple
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </div>
              {!!passwordError && <span className={classes.loginErrorText}>{passwordError}</span>}
            </div>

            <div className={classes.loginRow}>
              <Link href="/forget" className={classes.loginForgot}>{loginStrings.forgotPassword}</Link>
            </div>

            <Button
              type="submit"
              className={classes.loginSubmit}
              disabled={loading}
              onClick={onLogin}
              disableRipple
            >
              {loading ? loginStrings.loading : loginStrings.login}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
